// animate-status-effects.ts — nimateStatusEffectChanges is the data-driven
// status-effect badge renderer keyed off VISUAL_STATUS_EFFECTS (~250 UUIDs).
// Stackable overlays (maimed_1..10), elemental glows, hidden reveals, etc.

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ANIMATES STATUS EFFECT TRANSITIONS
// - Uses VISUAL_STATUS_EFFECTS constant for all UUID mappings
// - Data-driven: add new visual effects by extending the effects array
// - Supports single UUID or array of UUIDs (for grouped effects like maimed_1–10)
// - Keeps special reveal animation only for hidden
// - Clean removal with transitionend + safety timeout
// - Special handling for stackable 'maimed' (detect active maimed_N → orb count = N, escalation, limp)
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { encounterCreatureField, type EntityRow } from '../shared/array-types';

// QUANTITY-stack families (2026-08-25): maimed / blighted / shielded / mirror_image are ONE base row
// carrying `status_stack`; the numbered level rows below are legacy aliases. Read the count off the row.
function quantityStackFor(entity: unknown, baseUuid: string | undefined): number {
    if (!baseUuid || typeof ftCreatureStatuses !== 'function') return 0;
    const want = baseUuid.toUpperCase();
    const rows = ftCreatureStatuses(entity, 'target');
    if (!Array.isArray(rows)) return 0;
    for (const row of rows) {
        const id = String((row as any)?.status_item_id ?? (row as any)?.status_arr?.items?.item_id ?? '').toUpperCase();
        if (id !== want) continue;
        const raw = (row as any)?.status_stack;
        const n = Number(Array.isArray(raw) ? raw[0] : raw);
        return Number.isFinite(n) && n > 0 ? n : 1;
    }
    return 0;
}
const VSE_ANY = VISUAL_STATUS_EFFECTS as unknown as Record<string, string | undefined>;

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// STACKED WRAPPER-ANIMATION COMPOSER
//
// Every whole-token effect animation (fear shake, blighted pulse, tipsy wobble, …) is declared on
// the SAME element — the token's .position-relative wrapper — via equal-specificity
// `.position-relative:has(> .status-effect-overlay > .object-X)` rules in
// css/src/50-creature-status-fx.css. CSS `animation` is a single property, so with 2+ active
// effects the cascade keeps exactly ONE rule (the last one in the stylesheet) and every other
// effect's motion dies: frightened + blighted meant the fear sway stopped the moment blighted
// landed, because .object-blighted's rule sits later in the file.
//
// Fix: read those wrapper rules out of the loaded stylesheets ONCE, and whenever 2+ of them match
// a token's wrapper, write the UNION of their animation lists as an inline style (inline beats all
// the equal-specificity :has rules). With 0–1 matches the inline override is removed and plain CSS
// behavior resumes untouched. Matched rules are joined in stylesheet source order, so "later rule
// wins for a shared animated property" semantics are preserved (e.g. an escalation variant still
// masks its base pulse). Self-maintaining: new .object-* effects added to the CSS participate
// automatically — no JS registry to update.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface WrapperFxRule { selector: string; animation: string }

let wrapperFxRulesCache: WrapperFxRule[] | null = null;

/** Split a selector group on top-level commas (commas inside :has(...) parens don't split). */
function splitTopLevelSelectors(selectorText: string): string[] {
    const parts: string[] = [];
    let depth = 0;
    let cur = '';
    for (const ch of selectorText) {
        if (ch === '(') depth++;
        else if (ch === ')') depth--;
        if (ch === ',' && depth === 0) { parts.push(cur); cur = ''; continue; }
        cur += ch;
    }
    parts.push(cur);
    return parts.map(p => p.trim()).filter(Boolean);
}

/** Read the animation shorthand off a rule; rebuild from longhands if the engine won't serialize it. */
function ruleAnimationShorthand(style: CSSStyleDeclaration): string {
    const direct = style.getPropertyValue('animation').trim();
    if (direct) return direct;
    const names = style.getPropertyValue('animation-name').split(',').map(s => s.trim()).filter(Boolean);
    if (!names.length || (names.length === 1 && names[0] === 'none')) return '';
    const longhand = (prop: string) => style.getPropertyValue(prop).split(',').map(s => s.trim());
    const durations = longhand('animation-duration');
    const timings = longhand('animation-timing-function');
    const delays = longhand('animation-delay');
    const counts = longhand('animation-iteration-count');
    const pick = (arr: string[], i: number, fallback: string) => arr[i % arr.length] || fallback;
    return names
        .map((name, i) => `${pick(durations, i, '0s')} ${pick(timings, i, 'ease')} ${pick(delays, i, '0s')} ${pick(counts, i, '1')} ${name}`)
        .join(', ');
}

/** Collect (once) every wrapper-subject `:has(> .status-effect-overlay > …)` rule that sets an animation. */
function collectWrapperFxRules(): WrapperFxRule[] {
    if (wrapperFxRulesCache) return wrapperFxRulesCache;
    const rules: WrapperFxRule[] = [];
    for (const sheet of Array.from(document.styleSheets)) {
        let cssRules: CSSRuleList;
        try {
            cssRules = sheet.cssRules;
        } catch {
            continue; // cross-origin sheet
        }
        for (const rule of Array.from(cssRules)) {
            if (!(rule instanceof CSSStyleRule)) continue;
            const selText = rule.selectorText || '';
            if (!selText.includes(':has(') || !selText.includes('.status-effect-overlay')) continue;
            const anim = ruleAnimationShorthand(rule.style);
            if (!anim || anim === 'none') continue;
            for (const sel of splitTopLevelSelectors(selText)) {
                if (!sel.includes(':has(') || !sel.includes('.status-effect-overlay')) continue;
                // Wrapper-subject only: skip selectors whose subject descends past the :has() host
                // (e.g. `.position-relative:has(…) > img` — those animate children, not the wrapper).
                const afterHas = sel.slice(sel.lastIndexOf(')') + 1);
                if (/[\s>+~]/.test(afterHas)) continue;
                rules.push({ selector: sel, animation: anim });
            }
        }
    }
    // Don't cache an empty scan (e.g. style.css not parsed yet) — retry on the next call.
    if (rules.length) wrapperFxRulesCache = rules;
    return rules;
}

/** Apply/clear the composed inline animation on a token wrapper based on which fx rules match it. */
function composeStackedWrapperAnimations(wrapper: HTMLElement): void {
    const matched: string[] = [];
    for (const { selector, animation } of collectWrapperFxRules()) {
        try {
            if (wrapper.matches(selector)) matched.push(animation);
        } catch { /* selector unsupported by this engine — CSS wouldn't apply it either */ }
    }
    if (matched.length >= 2) {
        const composed = matched.join(', ');
        // Browsers normalize the shorthand on assignment, so compare against our own last
        // write (dataset) rather than style.animation to skip redundant per-update writes.
        if (wrapper.dataset.ftFxComposed !== composed) {
            wrapper.dataset.ftFxComposed = composed;
            wrapper.style.animation = composed;
        }
    } else if (wrapper.dataset.ftFxComposed) {
        delete wrapper.dataset.ftFxComposed;
        wrapper.style.removeProperty('animation');
    }
}

function animateStatusEffectChanges(
    el: HTMLElement,
    oldRef: EntityRow | HTMLElement | null | undefined,
    entity: EntityRow,
): boolean | void {
    const isInventory = el.classList.contains('inventory-item');
    const isCreature = !isInventory && !!(
        entity.encounter_creatures &&
        encounterCreatureField(entity.encounter_creatures, 'encounter_creature_id')
    );

    // Resolved permanent-status UUIDs, bridge-aware (normalized PC casters carry them in
    // computed.creature.abilities.status_effect_permanent — root stripped 2026-06-10;
    // monster/EC blobs still carry root or item_layer_types rows).
    const permanentStatusIds = ftStatusEffectPermanentUuids(entity);
    const effectIsPermanent = (uuids: string | readonly string[] | null | undefined): boolean => permanentStatusIds.length > 0 &&
        uuids != null &&
        (typeof uuids === 'string'
            ? permanentStatusIds.includes(uuids)
            : uuids.some(u => permanentStatusIds.includes(u)));

    // Find insert parent
    const insertParent: HTMLElement = isInventory
        ? el
        : (el.querySelector<HTMLElement>('.position-relative') || el);

    // Persistent overlay container
    let overlay = insertParent.querySelector<HTMLElement>('.status-effect-overlay');
    if (!overlay) {
        insertParent.insertAdjacentHTML('beforeend',
            '<div class="status-effect-overlay position-absolute top-0 start-0 w-100 h-100 pointer-events-none" style="z-index:200;"></div>'
        );
        overlay = insertParent.querySelector<HTMLElement>('.status-effect-overlay');
    }
    if (!overlay) {
        ftDebugLog('xz status', { warn: 'Unable to create status-effect overlay', el });
        return false;
    }

    // Recompose the stacked wrapper animation when effect divs are removed AFTER their fade-out
    // (the transitionend/timeout cleanups below run up to ~900ms after this function returns —
    // without this, a removed effect's motion would keep running via the stale inline style).
    if (!overlay.dataset.ftFxComposeObserved) {
        overlay.dataset.ftFxComposeObserved = '1';
        const wrapper = insertParent;
        new MutationObserver(() => composeStackedWrapperAnimations(wrapper)).observe(overlay, { childList: true });
    }

    // Escalation classes (maimed/blighted/shielded-moderate|severe, judgement tiers) must sit on the
    // SAME element the `.position-relative:has(…)` CSS rules select — for creatures that's the inner
    // wrapper, not `el`. Toggle both so either consumer keeps working.
    const toggleEscalationClass = (cls: string, on: boolean): void => {
        el.classList.toggle(cls, on);
        if (insertParent !== el) insertParent.classList.toggle(cls, on);
    };

    let hiddenWasRemoved = false;

    visualEffects.forEach(effect => {
        // === SPECIAL STACKABLE HANDLING FOR MAIMED ===
        if (effect.isStackable && effect.key === 'maimed') {
            const maimedUUIDs = effect.uuids || [];

            // Map numbered maimed UUIDs to stack count (maimed_1 = 1, maimed_2 = 2, etc.)
            let stackCount = 0;
            const numberedMaimed = [
                VISUAL_STATUS_EFFECTS.maimed_1,
                VISUAL_STATUS_EFFECTS.maimed_2,
                VISUAL_STATUS_EFFECTS.maimed_3,
                VISUAL_STATUS_EFFECTS.maimed_4,
                VISUAL_STATUS_EFFECTS.maimed_5,
                VISUAL_STATUS_EFFECTS.maimed_6,
                VISUAL_STATUS_EFFECTS.maimed_7,
                VISUAL_STATUS_EFFECTS.maimed_8,
                VISUAL_STATUS_EFFECTS.maimed_9,
                VISUAL_STATUS_EFFECTS.maimed_10
            ];

            for (let i = 0; i < numberedMaimed.length; i++) {
                if (hasStatusEffect(entity, numberedMaimed[i]!)) {
                    stackCount = i + 1; // maimed_1 → 1, maimed_2 → 2, etc.
                    break; // Assume only one numbered maimed active at a time
                }
            }
            if (stackCount === 0) stackCount = quantityStackFor(entity, VSE_ANY['maimed']);

            // Optional: if base 'maimed' is used for any stacks >0, could add logic here

            const shouldHave = stackCount > 0;
            const existing = overlay.querySelector<HTMLElement>(`.${effect.className}`);

            // Always update limp sway and escalation classes
            if (effect.creatureClass) {
                el.classList.toggle(effect.creatureClass, shouldHave);
            }
            toggleEscalationClass('maimed-moderate', stackCount >= 4);
            toggleEscalationClass('maimed-severe', stackCount >= 7);

            if (shouldHave) {
                // Always rebuild on every call when active (removes old, inserts new with current count)
                if (existing) {
                    existing.remove();
                }

                // Generate orbs (1 per stack, max 10)
                const maxOrbs = Math.min(stackCount, 10);
                let orbsHTML = '';
                for (let i = 0; i < maxOrbs; i++) {
                    const angle = (i / maxOrbs) * 360 + (Math.random() * 30 - 15);
                    const radius = 40 + (i % 4) * 8;
                    const x = 50 + radius * Math.cos(angle * Math.PI / 180);
                    const y = 50 + radius * Math.sin(angle * Math.PI / 180);
                    orbsHTML += `<span class="wound-orb stack-orb" style="top:${y}%; left:${x}%; animation-delay:${i * 0.3}s;"></span>`;
                }

                overlay.insertAdjacentHTML('beforeend', `<div class="${effect.className}">${orbsHTML}</div>`);
                const newDiv = overlay.querySelector<HTMLElement>(`.${effect.className}:last-child`);
                if (!newDiv) return;

                if (effect.addInitial) effect.addInitial(newDiv);
                requestAnimationFrame(() => {
                    newDiv.style.transition = effect.addTransition || 'opacity 0.6s ease-out';
                    if (effect.addTarget) effect.addTarget(newDiv);
                });
            } else if (existing) {
                existing.style.transition = effect.removeTransition || 'opacity 0.6s ease-out';
                existing.style.opacity = '0';

                const cleanup = () => {
                    if (existing.parentNode) existing.remove();
                };
                existing.addEventListener('transitionend', cleanup, { once: true });
                setTimeout(cleanup, 900);
            }

            return; // Skip normal handling
        }
		
		
        // === SPECIAL STACKABLE HANDLING FOR BLIGHTED ===
        if (effect.isStackable && effect.key === 'blighted') {
            const blightedUUIDs = effect.uuids || [];

            let stackCount = 0;
            const numberedBlighted = [
                VISUAL_STATUS_EFFECTS.blighted_1,
                VISUAL_STATUS_EFFECTS.blighted_2,
                VISUAL_STATUS_EFFECTS.blighted_3,
                VISUAL_STATUS_EFFECTS.blighted_4,
                VISUAL_STATUS_EFFECTS.blighted_5,
                VISUAL_STATUS_EFFECTS.blighted_6,
                VISUAL_STATUS_EFFECTS.blighted_7,
                VISUAL_STATUS_EFFECTS.blighted_8,
                VISUAL_STATUS_EFFECTS.blighted_9,
                VISUAL_STATUS_EFFECTS.blighted_10
            ];

            for (let i = 0; i < numberedBlighted.length; i++) {
                if (hasStatusEffect(entity, numberedBlighted[i]!)) {
                    stackCount = i + 1;
                    break;
                }
            }
            if (stackCount === 0) stackCount = quantityStackFor(entity, VSE_ANY['blighted']);

            const shouldHave = stackCount > 0;
            const existing = overlay.querySelector<HTMLElement>(`.${effect.className}`);

            toggleEscalationClass('blighted-moderate', stackCount >= 4);
            toggleEscalationClass('blighted-severe', stackCount >= 7);

            if (shouldHave) {
                if (existing) existing.remove();

                const maxWisps = Math.min(stackCount, 10);
                let wispsHTML = '';
                for (let i = 0; i < maxWisps; i++) {
                    const angle = (i / maxWisps) * 360 + (Math.random() * 30 - 15);
                    const radius = 40 + (i % 4) * 8;
                    const x = 50 + radius * Math.cos(angle * Math.PI / 180);
                    const y = 50 + radius * Math.sin(angle * Math.PI / 180);
                    wispsHTML += `<span class="necrotic-wisp stack-wisp" style="top:${y}%; left:${x}%; animation-delay:${i * 0.35}s;"></span>`;
                }

                overlay.insertAdjacentHTML('beforeend', `<div class="${effect.className}">${wispsHTML}</div>`);
                const newDiv = overlay.querySelector<HTMLElement>(`.${effect.className}:last-child`);
                if (!newDiv) return;

                if (effect.addInitial) effect.addInitial(newDiv);
                requestAnimationFrame(() => {
                    newDiv.style.transition = effect.addTransition || 'opacity 0.6s ease-out';
                    if (effect.addTarget) effect.addTarget(newDiv);
                });
            } else if (existing) {
                existing.style.transition = effect.removeTransition || 'opacity 0.6s ease-out';
                existing.style.opacity = '0';

                const cleanup = () => { if (existing.parentNode) existing.remove(); };
                existing.addEventListener('transitionend', cleanup, { once: true });
                setTimeout(cleanup, 900);
            }

            return;
        }	

        // === SPECIAL STACKABLE HANDLING FOR SHIELDED ===
        if (effect.isStackable && effect.key === 'shielded') {
            const shieldedUUIDs = effect.uuids || [];

            let stackCount = 0;
            const numberedShielded = [
                VISUAL_STATUS_EFFECTS.shielded_1,
                VISUAL_STATUS_EFFECTS.shielded_2,
                VISUAL_STATUS_EFFECTS.shielded_3,
                VISUAL_STATUS_EFFECTS.shielded_4,
                VISUAL_STATUS_EFFECTS.shielded_5,
                VISUAL_STATUS_EFFECTS.shielded_6,
                VISUAL_STATUS_EFFECTS.shielded_7,
                VISUAL_STATUS_EFFECTS.shielded_8,
                VISUAL_STATUS_EFFECTS.shielded_9,
                VISUAL_STATUS_EFFECTS.shielded_10
            ];

            for (let i = 0; i < numberedShielded.length; i++) {
                if (hasStatusEffect(entity, numberedShielded[i]!)) {
                    stackCount = i + 1;
                    break;
                }
            }
            if (stackCount === 0) stackCount = quantityStackFor(entity, VSE_ANY['shielded']);

            const shouldHave = stackCount > 0;
            const existing = overlay.querySelector<HTMLElement>(`.${effect.className}`);

            toggleEscalationClass('shielded-moderate', stackCount >= 4);
            toggleEscalationClass('shielded-severe', stackCount >= 7);

            if (shouldHave) {
                if (existing) existing.remove();

                const maxRings = Math.min(stackCount, 10);
                let ringsHTML = '';
                for (let i = 0; i < maxRings; i++) {
                    ringsHTML += `<div class="barrier-ring stack-ring"></div>`;
                }

                overlay.insertAdjacentHTML('beforeend', `<div class="${effect.className}">${ringsHTML}</div>`);
                const newDiv = overlay.querySelector<HTMLElement>(`.${effect.className}:last-child`);
                if (!newDiv) return;

                // === PERMANENT STATUS GOLDEN STAR (works for both normal & permanent arrays) ===
                if (newDiv) {
                    const isPermanent = effectIsPermanent(effect.uuids);

                    if (
                        isPermanent
                        && (effect.key as string) !== 'spider_climb_effect'
                        && (effect.key as string) !== 'ageless'
                    ) {
                        newDiv.classList.add('status-permanent');
                        newDiv.insertAdjacentHTML('beforeend', '<span class="permanent-star">★</span>');
                    }
                }

                if (effect.addInitial) effect.addInitial(newDiv);
                requestAnimationFrame(() => {
                    newDiv.style.transition = effect.addTransition || 'opacity 0.6s ease-out';
                    if (effect.addTarget) effect.addTarget(newDiv);
                });
            } else if (existing) {
                existing.style.transition = effect.removeTransition || 'opacity 0.6s ease-out';
                existing.style.opacity = '0';

                const cleanup = () => { if (existing.parentNode) existing.remove(); };
                existing.addEventListener('transitionend', cleanup, { once: true });
                setTimeout(cleanup, 900);
            }

            return;
        }		

        // === SPECIAL STACKABLE HANDLING FOR MIRROR IMAGE ===
        if (effect.isStackable && effect.key === 'mirror_image') {
            const mirrorUUIDs = effect.uuids || [];

            let stackCount = 0;
            const numberedMirror = [
                VISUAL_STATUS_EFFECTS.mirror_image_1,
                VISUAL_STATUS_EFFECTS.mirror_image_2,
                VISUAL_STATUS_EFFECTS.mirror_image_3
            ];

            for (let i = 0; i < numberedMirror.length; i++) {
                if (hasStatusEffect(entity, numberedMirror[i]!)) {
                    stackCount = i + 1;
                    break;
                }
            }
            if (stackCount === 0) stackCount = quantityStackFor(entity, VSE_ANY['mirror_image_effect']);

            // Fallback if base effect active (treat as 3 images remaining)
            const baseMirrorImageUuid = VISUAL_STATUS_EFFECTS.mirror_image_effect;
            if (stackCount === 0 && baseMirrorImageUuid && hasStatusEffect(entity, baseMirrorImageUuid)) {
                stackCount = 3;
            }

            const shouldHave = stackCount > 0;
            const existing = overlay.querySelector<HTMLElement>(`.${effect.className}`);

            if (shouldHave) {
                if (existing) existing.remove();

                // Map stack count to correct icon
                const iconMap: Record<number, string> = {
                    1: 'mirror_image_1.png',
                    2: 'mirror_image_2.png',
                    3: 'mirror_image_3.png'
                };
                const iconFile = iconMap[stackCount] || 'mirror_image_3.png'; // Default to 3
                const iconURL = `${BASE_URL_CDN}images/icons/${iconFile}`;

                const iconHTML = `
                    <img src="${iconURL}" class="mirror-icon" alt="Mirror Images Remaining">
                `;

                overlay.insertAdjacentHTML('beforeend', `<div class="${effect.className}">${iconHTML}</div>`);
                const newDiv = overlay.querySelector<HTMLElement>(`.${effect.className}:last-child`);
                if (!newDiv) return;

                // === PERMANENT STATUS GOLDEN STAR (works for both normal & permanent arrays) ===
                if (newDiv) {
                    const isPermanent = effectIsPermanent(effect.uuids);

                    if (
                        isPermanent
                        && (effect.key as string) !== 'spider_climb_effect'
                        && (effect.key as string) !== 'ageless'
                    ) {
                        newDiv.classList.add('status-permanent');
                        newDiv.insertAdjacentHTML('beforeend', '<span class="permanent-star">★</span>');
                    }
                }

                if (effect.addInitial) effect.addInitial(newDiv);
                requestAnimationFrame(() => {
                    newDiv.style.transition = effect.addTransition || 'opacity 0.6s ease-out';
                    if (effect.addTarget) effect.addTarget(newDiv);
                });
            } else if (existing) {
                existing.style.transition = effect.removeTransition || 'opacity 0.6s ease-out';
                existing.style.opacity = '0';

                const cleanup = () => { if (existing.parentNode) existing.remove(); };
                existing.addEventListener('transitionend', cleanup, { once: true });
                setTimeout(cleanup, 900);
            }

            return;
        }

        // === SPECIAL STACKABLE HANDLING FOR JUDGEMENT ===
        if (effect.isStackable && effect.key === 'judgement') {
            let stackCount = 0;
            const numberedJudgement = [
                VISUAL_STATUS_EFFECTS.judgement_1,
                VISUAL_STATUS_EFFECTS.judgement_2,
                VISUAL_STATUS_EFFECTS.judgement_3
            ];

            for (let i = 0; i < numberedJudgement.length; i++) {
                if (hasStatusEffect(entity, numberedJudgement[i]!)) {
                    stackCount = i + 1;
                    break;
                }
            }

            // Fallback: base 'judgement' UUID present without a numbered variant → treat as stack 1
            const baseJudgementUuid = VISUAL_STATUS_EFFECTS.judgement;
            if (stackCount === 0 && baseJudgementUuid && hasStatusEffect(entity, baseJudgementUuid)) {
                stackCount = 1;
            }

            const shouldHave = stackCount > 0;
            const existing = overlay.querySelector<HTMLElement>(`.${effect.className}`);

            // Tier escalation classes (drive aura / tremor)
            toggleEscalationClass('judgement-bound',     stackCount >= 1);
            toggleEscalationClass('judgement-marked',    stackCount >= 2);
            toggleEscalationClass('judgement-condemned', stackCount >= 3);

            if (shouldHave) {
                if (existing) existing.remove();

                // Single seal above the head; Roman numeral inside marks the stack count (I / II / III).
                // The outer ring rotates; the numeral counter-rotates so it stays upright (see CSS).
                const numeralMap: Record<number, string> = { 1: 'I', 2: 'II', 3: 'III' };
                const numeral = numeralMap[stackCount] || 'I';
                const sealHTML = `<div class="judgement-seal stack-seal"><span class="judgement-numeral">${numeral}</span></div>`;

                overlay.insertAdjacentHTML('beforeend', `<div class="${effect.className}">${sealHTML}</div>`);
                const newDiv = overlay.querySelector<HTMLElement>(`.${effect.className}:last-child`);
                if (!newDiv) return;

                // === PERMANENT STATUS GOLDEN STAR (mirror shielded/mirror_image contract) ===
                if (newDiv) {
                    const isPermanent = effectIsPermanent(effect.uuids);

                    if (isPermanent) {
                        newDiv.classList.add('status-permanent');
                        newDiv.insertAdjacentHTML('beforeend', '<span class="permanent-star">★</span>');
                    }
                }

                if (effect.addInitial) effect.addInitial(newDiv);
                requestAnimationFrame(() => {
                    newDiv.style.transition = effect.addTransition || 'opacity 0.6s ease-out';
                    if (effect.addTarget) effect.addTarget(newDiv);
                });
            } else if (existing) {
                existing.style.transition = effect.removeTransition || 'opacity 0.6s ease-out';
                existing.style.opacity = '0';

                const cleanup = () => { if (existing.parentNode) existing.remove(); };
                existing.addEventListener('transitionend', cleanup, { once: true });
                setTimeout(cleanup, 900);
            }

            return;
        }

        // === NORMAL EFFECT HANDLING ===
        let shouldHave;
        if (effect.key === 'private') {
            shouldHave = checkTargetPrivate(entity);
        } else {
            if (!effect.uuids) return;
            shouldHave = Array.isArray(effect.uuids)
                ? effect.uuids.some(uuid => hasStatusEffect(entity, uuid))
                : hasStatusEffect(entity, effect.uuids);
        }

        // Immobile chains: require an active layered Immobile instance (spell, encumbrance, etc.).
        // Do not show from `status_effect_permanent` alone or from inferred movement stats.
        if (effect.key === 'immobile') {
            shouldHave = Array.isArray(effect.uuids)
                ? effect.uuids.some(uuid => hasLayeredStatusEffect(entity, uuid))
                : hasLayeredStatusEffect(entity, effect.uuids ?? '');
        }

        const existing = overlay.querySelector<HTMLElement>(`.${effect.className}`);

        if (effect.creatureClass) {
            el.classList.toggle(effect.creatureClass, shouldHave);
        }

        if (shouldHave && !existing) {
            overlay.insertAdjacentHTML('beforeend', effect.html);
            const newDiv = overlay.querySelector<HTMLElement>(`.${effect.className}:last-child`);
            if (!newDiv) return;

            if (effect.addInitial) effect.addInitial(newDiv);
            requestAnimationFrame(() => {
                newDiv.style.transition = effect.addTransition || 'opacity 0.6s ease-out';
                if (effect.addTarget) effect.addTarget(newDiv);
            });

        } else if (!shouldHave && existing) {
            if (effect.isSpecialReveal && effect.key === 'hidden') {
                hiddenWasRemoved = true;
            }

            if (effect.removeInitial) effect.removeInitial(existing);

            existing.style.transition = effect.removeTransition || 'opacity 0.6s ease-out';
            existing.style.opacity = '0';

            const cleanup = () => {
                if (existing.parentNode) existing.remove();
            };

            existing.addEventListener('transitionend', cleanup, { once: true });
            setTimeout(cleanup, 900);
        }
    });

    if (isCreature) {
        const isDead = encounterCreatureField<number>(entity.encounter_creatures, 'encounter_creature_dead') === 1;
        // Class is the canonical DOM dead-state (CSS hide-dead rule + tokenIsDead fallback);
        // the data-encounter_creature_dead copy was retired with the token data contract (2026-06-12).
        el.classList.toggle('encounter-creature-dead', isDead);
    }

    // With all effect divs + escalation classes settled, compose the wrapper's stacked animation
    // (multiple simultaneous whole-token effects would otherwise cancel each other — see header).
    composeStackedWrapperAnimations(insertParent);

    if (hiddenWasRemoved) {
        el.style.transition = 'none';
        el.style.opacity = '0';
        el.style.transform = 'scale(0.85) translateY(40px)';
        el.style.filter = 'blur(5px) brightness(1.8)';
        void el.offsetHeight;

        requestAnimationFrame(() => {
            el.style.transition =
                'opacity 1.2s cubic-bezier(0.16, 1, 0.3, 1), ' +
                'transform 1.2s cubic-bezier(0.16, 1, 0.3, 1), ' +
                'filter 1s ease-out';
            el.style.opacity = '1';
            el.style.transform = 'scale(1) translateY(0)';
            el.style.filter = 'blur(0) brightness(1)';
        });
    }

    return true;
}

// ftComposeStackedWrapperAnimations exported for the staging probe
// (tools/test/status-fx-stacking-probe.html) and console debugging.
Object.assign(window, {
    animateStatusEffectChanges,
    ftComposeStackedWrapperAnimations: composeStackedWrapperAnimations,
});

export { animateStatusEffectChanges };
